import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, FileText, Mail, Ticket, Calendar, CreditCard } from 'lucide-react';
import { SyncRecord, SyncSource, CHANGE_BADGE, SOURCES, PreviewPayload, ClassificationPayload, SummaryPayload, relativeTime } from './types';
import { RecordDiff } from './RecordDiff';
const SOURCE_ICONS: Record<SyncSource, React.FC<{
  size?: number;
  className?: string;
}>> = {
  confluence: FileText,
  gmail: Mail,
  jira: Ticket,
  gcal: Calendar,
  quickbooks: CreditCard
};
type TabId = 'preview' | 'diff' | 'classification' | 'summary';
interface Props {
  record: SyncRecord | null;
  onClose: () => void;
}
export function RecordSheet({
  record,
  onClose
}: Props) {
  const [tab, setTab] = useState<TabId>('preview');
  useEffect(() => {
    setTab('preview');
  }, [record?.id]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <AnimatePresence data-id="element-1852">
      {record && <motion.div key="record-sheet-backdrop" className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center" initial={{
      opacity: 0,
      pointerEvents: 'auto'
    }} animate={{
      opacity: 1,
      pointerEvents: 'auto'
    }} exit={{
      opacity: 0,
      pointerEvents: 'none'
    }} onClick={onClose} data-id="element-1853">
          <motion.div className="bg-[#141418] w-full max-w-6xl h-[92vh] rounded-t-[20px] border-t border-x border-white/[0.06] flex flex-col" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 320,
        damping: 34
      }} onClick={e => e.stopPropagation()} data-id="element-1854">
            <Header record={record} onClose={onClose} data-id="element-1855" />
            <Tabs record={record} tab={tab} onChange={setTab} data-id="element-1856" />
            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-1857">
              {tab === 'preview' && <PreviewTab preview={record.preview} data-id="element-1858" />}
              {tab === 'diff' && record.diff && <RecordDiff diff={record.diff} data-id="element-1859" />}
              {tab === 'classification' && record.classification && <ClassificationTab classification={record.classification} data-id="element-1860" />}
              {tab === 'summary' && record.summary && <SummaryTab summary={record.summary} data-id="element-1861" />}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
function Header({
  record,
  onClose
}: {
  record: SyncRecord;
  onClose: () => void;
}) {
  const Icon = SOURCE_ICONS[record.source];
  const sourceMeta = SOURCES.find(s => s.id === record.source);
  const sourceLabel = sourceMeta?.label ?? record.source;
  const badge = CHANGE_BADGE[record.changeType];
  return <div className="flex items-center justify-between px-6 pt-6 pb-4 gap-4 border-b border-white/[0.06]" data-id="element-1862">
      <div className="flex items-center gap-3 min-w-0" data-id="element-1863">
        <div className="w-10 h-10 rounded-[10px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-1864">
          <Icon size={18} className="text-[#8a8680]" data-id="element-1865" />
        </div>
        <div className="min-w-0" data-id="element-1866">
          <div className="text-[18px] font-medium text-[#e8e4df] truncate" data-id="element-1867">{record.title}</div>
          <div className="flex items-center gap-2 mt-1" data-id="element-1868">
            <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#252530] text-[#8a8680]" data-id="element-1869">{sourceLabel}</span>
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full font-medium ${badge.classes}`} data-id="element-1870">
              {badge.label}
            </span>
            <span className="text-[11px] text-[#8a8680]" data-id="element-1871">arrived {relativeTime(record.arrivedAt)}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0" data-id="element-1872">
        <a href={record.externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[13px] text-[#8a8680] hover:text-[#4a9a9a]" data-id="element-1873">
          <ExternalLink size={14} data-id="element-1874" />
          View in {sourceLabel}
        </a>
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/5 rounded-lg" data-id="element-1875">
          <X size={18} className="text-[#8a8680]" data-id="element-1876" />
        </button>
      </div>
    </div>;
}
function Tabs({
  record,
  tab,
  onChange
}: {
  record: SyncRecord;
  tab: TabId;
  onChange: (t: TabId) => void;
}) {
  const visibleTabs: {
    id: TabId;
    label: string;
  }[] = [{
    id: 'preview',
    label: 'Preview'
  }];
  if (record.diff) visibleTabs.push({
    id: 'diff',
    label: 'Diff'
  });
  if (record.classification) visibleTabs.push({
    id: 'classification',
    label: 'Classification'
  });
  if (record.summary) visibleTabs.push({
    id: 'summary',
    label: 'Summary'
  });
  return <nav className="flex items-center gap-6 px-6 border-b border-[#8a8680]/10" data-id="element-1877">
      {visibleTabs.map(({
      id,
      label
    }) => <button key={id} onClick={() => onChange(id)} className={`pb-3 pt-2 text-[14px] font-medium transition-colors relative ${tab === id ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-1878">
          {label}
          {tab === id && <motion.div layoutId="recordSheetTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
        type: 'spring',
        stiffness: 500,
        damping: 30
      }} data-id="element-1879" />}
        </button>)}
    </nav>;
}
function PreviewTab({
  preview
}: {
  preview: PreviewPayload;
}) {
  switch (preview.source) {
    case 'confluence':
      return <ConfluencePreview markdown={preview.markdown} data-id="element-1880" />;
    case 'gmail':
      return <GmailPreview from={preview.from} to={preview.to} subject={preview.subject} body={preview.body} data-id="element-1881" />;
    case 'jira':
      return <JiraPreview fields={preview.fields} description={preview.description} data-id="element-1882" />;
    case 'gcal':
      return <GcalPreview start={preview.start} end={preview.end} attendees={preview.attendees} location={preview.location} description={preview.description} data-id="element-1883" />;
    case 'quickbooks':
      return <QuickbooksPreview number={preview.number} customer={preview.customer} lineItems={preview.lineItems} total={preview.total} status={preview.status} data-id="element-1884" />;
  }
}
function ConfluencePreview({
  markdown
}: {
  markdown: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1885">
      <pre className="whitespace-pre-wrap text-[13px] text-[#c4bfb8] leading-relaxed font-sans" data-id="element-1886">{markdown}</pre>
    </div>;
}
function GmailPreview({
  from,
  to,
  subject,
  body
}: {
  from: string;
  to: string;
  subject: string;
  body: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1887">
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-1888">
        <span className="w-16 text-[#8a8680]" data-id="element-1889">From</span>
        <span className="text-[#e8e4df]" data-id="element-1890">{from}</span>
      </div>
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-1891">
        <span className="w-16 text-[#8a8680]" data-id="element-1892">To</span>
        <span className="text-[#e8e4df]" data-id="element-1893">{to}</span>
      </div>
      <div className="flex gap-3 py-1.5 text-[12px] border-b border-white/[0.04]" data-id="element-1894">
        <span className="w-16 text-[#8a8680]" data-id="element-1895">Subject</span>
        <span className="text-[#e8e4df]" data-id="element-1896">{subject}</span>
      </div>
      <div className="mt-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-1897">{body}</div>
    </div>;
}
function JiraPreview({
  fields,
  description
}: {
  fields: {
    label: string;
    value: string;
  }[];
  description: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1898">
      <div className="grid grid-cols-2 gap-6" data-id="element-1899">
        <div data-id="element-1900">
          {fields.map((f, i) => <div key={i} className="flex items-center justify-between py-2.5 border-b border-white/[0.04] last:border-b-0" data-id="element-1901">
              <span className="text-[13px] text-[#8a8680]" data-id="element-1902">{f.label}</span>
              <span className="text-[13px] text-[#e8e4df] font-medium" data-id="element-1903">{f.value}</span>
            </div>)}
        </div>
        <div className="bg-[#252530] rounded-[12px] p-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-1904">
          {description}
        </div>
      </div>
    </div>;
}
function GcalPreview({
  start,
  end,
  attendees,
  location,
  description
}: {
  start: string;
  end: string;
  attendees: string[];
  location: string;
  description: string;
}) {
  const tiles: {
    label: string;
    value: string;
  }[] = [{
    label: 'Start',
    value: start
  }, {
    label: 'End',
    value: end
  }, {
    label: 'Location',
    value: location
  }, {
    label: 'Attendees',
    value: attendees.join(', ')
  }];
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1905">
      <div className="grid grid-cols-2 gap-3" data-id="element-1906">
        {tiles.map(t => <div key={t.label} className="bg-[#252530] rounded-[12px] p-4" data-id="element-1907">
            <div className="text-[11px] text-[#8a8680] uppercase tracking-wider" data-id="element-1908">{t.label}</div>
            <div className="text-[13px] text-[#e8e4df] mt-1" data-id="element-1909">{t.value}</div>
          </div>)}
      </div>
      <div className="mt-4 text-[13px] text-[#c4bfb8] leading-relaxed whitespace-pre-wrap" data-id="element-1910">{description}</div>
    </div>;
}
function QuickbooksPreview({
  number,
  customer,
  lineItems,
  total,
  status
}: {
  number: string;
  customer: string;
  lineItems: {
    label: string;
    amount: string;
  }[];
  total: string;
  status: string;
}) {
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1911">
      <div className="flex items-center justify-between" data-id="element-1912">
        <div className="text-[16px] font-medium text-[#e8e4df]" data-id="element-1913">{number}</div>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#4a9a9a]/20 text-[#4a9a9a] uppercase tracking-wider" data-id="element-1914">
          {status}
        </span>
      </div>
      <div className="text-[13px] text-[#8a8680] mt-1" data-id="element-1915">Customer: {customer}</div>
      <div className="mt-4" data-id="element-1916">
        {lineItems.map((li, i) => <div key={i} className="flex justify-between py-2 border-b border-white/[0.04] text-[13px]" data-id="element-1917">
            <span className="text-[#c4bfb8]" data-id="element-1918">{li.label}</span>
            <span className="text-[#e8e4df]" data-id="element-1919">{li.amount}</span>
          </div>)}
      </div>
      <div className="flex justify-between pt-4 text-[14px] font-medium text-[#e8e4df]" data-id="element-1920">
        <span data-id="element-1921">Total</span>
        <span data-id="element-1922">{total}</span>
      </div>
    </div>;
}
function ClassificationTab({
  classification
}: {
  classification: ClassificationPayload;
}) {
  const [reclassifying, setReclassifying] = useState(false);
  const handleReclassify = () => {
    setReclassifying(true);
    setTimeout(() => setReclassifying(false), 1500);
  };
  return <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1923">
      <span className="inline-block px-3 py-1 rounded-full bg-[#4a9a9a]/20 text-[#4a9a9a] text-[13px] font-medium" data-id="element-1924">
        {classification.label}
      </span>
      <div className="flex items-center gap-3 mt-4" data-id="element-1925">
        <div className="flex-1 h-2 rounded-full bg-[#252530] overflow-hidden" data-id="element-1926">
          <div className="h-full bg-[#4a9a9a]" style={{
          width: `${classification.confidence * 100}%`
        }} data-id="element-1927" />
        </div>
        <span className="text-[12px] text-[#8a8680] shrink-0" data-id="element-1928">
          {Math.round(classification.confidence * 100)}%
        </span>
      </div>
      <p className="text-[12px] text-[#8a8680] mt-3" data-id="element-1929">
        classified by {classification.model} · {classification.latencyMs}ms
      </p>
      <p className="text-[14px] text-[#c4bfb8] leading-relaxed mt-4" data-id="element-1930">{classification.reasoning}</p>
      <button onClick={handleReclassify} className="mt-6 px-4 py-2 rounded-[10px] bg-[#252530] text-[#e8e4df] text-[13px] hover:bg-[#2a2a35] transition-colors disabled:opacity-50" disabled={reclassifying} data-id="element-1931">
        {reclassifying ? 'Reclassifying…' : 'Reclassify'}
      </button>
    </div>;
}
function SummaryTab({
  summary
}: {
  summary: SummaryPayload;
}) {
  return <div data-id="element-1932">
      <p className="text-[12px] text-[#8a8680] mb-4" data-id="element-1933">
        Indexed as {summary.chunkCount} chunks · {summary.embeddingModel}
      </p>
      <div className="bg-[#1c1c22] rounded-[16px] p-6" data-id="element-1934">
        <p className="text-[14px] text-[#c4bfb8] leading-relaxed" data-id="element-1935">{summary.summary}</p>
      </div>
      <a href="#" className="inline-flex items-center gap-1.5 mt-4 text-[13px] text-[#8a8680] hover:text-[#4a9a9a] transition-colors" data-id="element-1936">
        <ExternalLink size={14} data-id="element-1937" />
        Open full document
      </a>
    </div>;
}